import * as yup from 'yup';

import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { Button, Input } from '../../../components';
import { Card, CardBody, CardFooter, Col, Form, Label, Row } from 'reactstrap';
import { EnrollmentRetention, EnrollmentRetentionDetail, ProgramDemographics } from '../../../common/Types';
import React, { useEffect, useState } from 'react';

import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

const Demographics = (props: AnnualReportProps) => {
    const [demographics, setDemographics] = useState<ProgramDemographics[]>();
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [enrollmentRetention, setEnrollmentRetention] = useState<EnrollmentRetention[]>();
    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );

    const defaultYear = props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1;

    const demographicSchema = yup
        .object({
            demographicsId: yup.number().required().default(0),
            programId: yup.number().required().default(props.program.programId),
            graduatingYear: yup.number().required().default(defaultYear),
            gradMale: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradFemale: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradGenderNoResponse: yup
                .number()
                .required('This field is required')
                .min(0)
                .typeError('This field is required'),
            gradGenderNotListed: yup
                .number()
                .required('This field is required')
                .min(0)
                .typeError('This field is required'),
            gradLessThan25: yup.number().required('This field is required').min(0).typeError('This field is required'),
            grad25To35: yup.number().required('This field is required').min(0).typeError('This field is required'),
            grad36to45: yup.number().required('This field is required').min(0).typeError('This field is required'),
            grad45OrMore: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradUnknown: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradAfricanAmerican: yup
                .number()
                .required('This field is required')
                .min(0)
                .typeError('This field is required'),
            gradAmericanIndian: yup
                .number()
                .required('This field is required')
                .min(0)
                .typeError('This field is required'),
            gradAsian: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradCaucasian: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradHispanic: yup.number().required('This field is required').min(0).typeError('This field is required'),
            gradOther: yup.number().required('This field is required').min(0).typeError('This field is required'),
            createdBy: yup.string().notRequired().nullable(),
            dateCreated: yup.date().notRequired().nullable(),
            lastEdited: yup.date().notRequired().nullable(),
            editedBy: yup.string().notRequired().nullable(),
            totalGrads: yup.number().notRequired().nullable(),
            genderValidate: yup
                .mixed()
                .optional()
                .nullable()
                .test('gender-total', 'Gender breakdown should equal total graduates', function () {
                    const { gradMale, gradFemale, gradGenderNoResponse, gradGenderNotListed, totalGrads } = this.parent;
                    const genderSum =
                        (+gradMale || 0) +
                        (+gradFemale || 0) +
                        (+gradGenderNoResponse || 0) +
                        (+gradGenderNotListed || 0);
                    const expectedGrads = +totalGrads || 0;
                    return genderSum === expectedGrads;
                }),
            ageValidate: yup
                .mixed()
                .optional()
                .nullable()
                .test('age-total', 'Age breakdown should equal total graduates', function () {
                    const { gradLessThan25, grad25To35, grad36to45, grad45OrMore, gradUnknown, totalGrads } = this.parent;
                    const ageSum =
                        (+gradLessThan25 || 0) +
                        (+grad25To35 || 0) +
                        (+grad36to45 || 0) +
                        (+grad45OrMore || 0) +
                        (+gradUnknown || 0);
                    const expectedGrads = +totalGrads || 0;
                    return ageSum === expectedGrads;
                }),
            ethnicityValidate: yup
                .mixed()
                .optional()
                .nullable()
                .test('ethnicity-total', 'Ethnicity breakdown should equal total graduates', function () {
                    const {
                        gradAfricanAmerican,
                        gradAmericanIndian,
                        gradAsian,
                        gradCaucasian,
                        gradHispanic,
                        gradOther,
                        totalGrads,
                    } = this.parent;
                    const ethnicitySum =
                        (+gradAfricanAmerican || 0) +
                        (+gradAmericanIndian || 0) +
                        (+gradAsian || 0) +
                        (+gradCaucasian || 0) +
                        (+gradHispanic || 0) +
                        (+gradOther || 0);
                    const expectedGrads = +totalGrads || 0;
                    return ethnicitySum === expectedGrads;
                }),
        })
        .defined();

    const {
        handleSubmit,
        control,
        getValues,
        reset,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(demographicSchema as any),
        defaultValues: {
            demographicsId: 0,
            programId: props.program.programId,
            graduatingYear: defaultYear,
            gradMale: 0,
            gradFemale: 0,
            gradGenderNoResponse: 0,
            gradGenderNotListed: 0,
            gradLessThan25: 0,
            grad25To35: 0,
            grad36to45: 0,
            grad45OrMore: 0,
            gradUnknown: 0,
            gradAfricanAmerican: 0,
            gradAmericanIndian: 0,
            gradAsian: 0,
            gradCaucasian: 0,
            gradHispanic: 0,
            gradOther: 0,
            createdBy: null,
            dateCreated: new Date(),
            lastEdited: new Date(),
            editedBy: null,
            totalGrads: 0,
            genderValidate: null,
            ageValidate: null,
            ethnicityValidate: null,
        } as any,
    });

    useEffect(() => {
        const getDemographics = () => {
            programService
                .getProgramDemographics(
                    props.program.programId,
                    props.reportYear ?? new Date().getFullYear(),
                    props.settings.reportingWindow.reportingWindow || 1,
                )
                .then((response) => {
                    setDemographics(response);
                    const surveyYear = props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1;
                    const currentYearDemo = response?.find((d) => d.graduatingYear === surveyYear);
                    if (currentYearDemo) {
                        reset(currentYearDemo);
                    }
                })
                .catch(() => {
                    toast.error('Unable to get demographics.');
                });
        };

        const getEnrollmentRetention = () => {
            if (props.program.programId && props.reportYear && props.settings.reportingWindow) {
                programService
                    .getEnrollmentRetentionDataByProgramIdReportYearReportWindow(
                        props.program.programId,
                        +props.reportYear,
                        props.settings.reportingWindow.reportingWindow || 0,
                        props.settings?.reportingWindow?.yearAvarageStart || +props.reportYear - 1,
                    )
                    .then((result) => {
                        setEnrollmentRetention(result);
                    })
                    .catch(() => {
                        toast.error('Error loading graduate detail.');
                    });
            }
        };

        getDemographics();
        getEnrollmentRetention();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        props.program.programId,
        props.reportYear,
        props.settings.reportingWindow.reportingWindow,
        props.settings.reportingWindow.yearAvarageStart,
    ]);

    // Sync totalGrads into form state when enrollment data or demographics change
    // Must re-run after demographics loads because reset(currentYearDemo) overwrites totalGrads
    useEffect(() => {
        if (enrollmentRetention) {
            const surveyYear = props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1;
            const grads = getGraduatesByYear(surveyYear);
            setValue('totalGrads', grads);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enrollmentRetention, demographics]);

    const onSubmit = (formData) => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving demographics...');

        programService
            .saveProgramDemographics(props.program.programId, formData)
            .then((result) => {
                // Update form with server response (especially demographicsId for new records)
                reset(result);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Demographics Saved.',
                });

                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save demographics.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const submitWithoutValidation = () => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving demographics...');

        programService
            .saveProgramDemographics(props.program.programId, getValues() as ProgramDemographics)
            .then((result) => {
                // Update form with server response (especially demographicsId for new records)
                reset(result);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Demographics Saved.',
                });

                if (demographics && demographics.length > 0) {
                    const demo = demographics.find((d) => d.graduatingYear === result.graduatingYear);

                    if (demo) {
                        const index = demographics.indexOf(demo, 0);
                        const newList = [...demographics];
                        newList.splice(index, 1, result);
                        setDemographics(newList);
                    } else {
                        setDemographics([...demographics, result]);
                    }
                } else {
                    setDemographics([result]);
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Failed to save demographics.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const getEachReportYear = (): number[] => {
        const years = [] as number[];

        for (
            let i = props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1;
            i >=
            (props.settings.reportingWindow.yearAvarageStart || +props.reportYear) -
                (props.settings.reportingWindow.reportingWindow || 1);
            i--
        ) {
            years.push(i);
        }

        return years;
    };

    const getGraduatesByYear = (graduationYear: number): number => {
        if (enrollmentRetention) {
            const cohorts = enrollmentRetention.map((c) => {
                return c.enrollmentRetentionDetailsDto || [];
            });
            const cohortGrads = ([] as EnrollmentRetentionDetail[]).concat
                .apply([], cohorts)
                .filter((c) => c.graduationYear === graduationYear);

            let graduates = 0;

            cohortGrads.forEach((c) => {
                graduates += c.graduated || 0;
            });

            return graduates;
        }

        return 0;
    };

    const getDemographicsByGraduationYear = (year: number): ProgramDemographics | undefined => {
        if (demographics) {
            return demographics.find((e) => e.graduatingYear === year);
        }

        return undefined;
    };

    return (
        <Card>
            {enrollmentRetention && demographics && (
                <Form noValidate onSubmit={handleSubmit(onSubmit, (validationErrors) => {
                    const fieldNames = Object.keys(validationErrors);
                    toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
                })}>
                    <CardBody>
                        <h3 className="card-title">Demographics</h3>
                        <Row>
                            <Col>
                                <div className={'table-responsive'}>
                                    <table className={'table table-striped'}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '25%' }}>{`Graduation Year`}</th>
                                                {getEachReportYear().map((year) => (
                                                    <th
                                                        style={{
                                                            width: `${
                                                                75 /
                                                                (props.settings.reportingWindow.reportingWindow || 1)
                                                            }%`,
                                                        }}
                                                        key={year}
                                                    >
                                                        {year}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>{`Graduates`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        <span>{getGraduatesByYear(year)}</span>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <Controller
                                                                name="totalGrads"
                                                                control={control}
                                                                render={({ field }) => (
                                                                    <input
                                                                        type="hidden"
                                                                        {...field}
                                                                        value={getGraduatesByYear(year)}
                                                                    />
                                                                )}
                                                            />
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td
                                                    colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 2}
                                                ></td>
                                            </tr>
                                            <tr>
                                                <td colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 2}>
                                                    <Label>{`Gender`}</Label>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{`Male`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradMale"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradMale?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradMale.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradMale}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Female`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradFemale"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradFemale?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradFemale.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradFemale}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Prefer Not to Respond`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradGenderNoResponse"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradGenderNoResponse?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {
                                                                                errors.gradGenderNoResponse
                                                                                    .message as string
                                                                            }
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {
                                                                    getDemographicsByGraduationYear(year)
                                                                        ?.gradGenderNoResponse
                                                                }
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Gender Not Listed`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradGenderNotListed"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradGenderNotListed?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {
                                                                                errors.gradGenderNotListed
                                                                                    .message as string
                                                                            }
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {
                                                                    getDemographicsByGraduationYear(year)
                                                                        ?.gradGenderNotListed
                                                                }
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td></td>
                                                <td colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 2}>
                                                    {errors?.genderValidate?.message && (
                                                        <div>
                                                            <Label className={'text-danger'}>
                                                                {errors.genderValidate.message as string}
                                                            </Label>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 2}>
                                                    <Label>{`Age`}</Label>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{`Age less than 25 years`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradLessThan25"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradLessThan25?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradLessThan25.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradLessThan25}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`25 to 35 years`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="grad25To35"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.grad25To35?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.grad25To35.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.grad25To35}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`36 to 45 years`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="grad36to45"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.grad36to45?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.grad36to45.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.grad36to45}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`46 or more years`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="grad45OrMore"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.grad45OrMore?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.grad45OrMore.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.grad45OrMore}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Unknown`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradUnknown"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradUnknown?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradUnknown.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradUnknown}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td></td>
                                                <td colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 2}>
                                                    {errors?.ageValidate?.message && (
                                                        <div>
                                                            <Label className={'text-danger'}>
                                                                {errors.ageValidate.message as string}
                                                            </Label>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            <tr>
                                                <td colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 2}>
                                                    <Label>{`Ethnicity`}</Label>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>{`African American`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradAfricanAmerican"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradAfricanAmerican?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {
                                                                                errors.gradAfricanAmerican
                                                                                    .message as string
                                                                            }
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {
                                                                    getDemographicsByGraduationYear(year)
                                                                        ?.gradAfricanAmerican
                                                                }
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`American Indian`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradAmericanIndian"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradAmericanIndian?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {
                                                                                errors.gradAmericanIndian
                                                                                    .message as string
                                                                            }
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {
                                                                    getDemographicsByGraduationYear(year)
                                                                        ?.gradAmericanIndian
                                                                }
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Asian`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradAsian"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradAsian?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradAsian.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradAsian}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Caucasian`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradCaucasian"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradCaucasian?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradCaucasian.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradCaucasian}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Hispanic`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradHispanic"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradHispanic?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradHispanic.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradHispanic}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td>{`Other`}</td>
                                                {getEachReportYear().map((year) => (
                                                    <td key={year}>
                                                        {year === props.settings.reportingWindow.yearAvarageStart && (
                                                            <>
                                                                <Controller
                                                                    name="gradOther"
                                                                    control={control}
                                                                    render={({ field }) => (
                                                                        <Input
                                                                            type="number"
                                                                            {...field}
                                                                            value={field.value ?? ''}
                                                                        />
                                                                    )}
                                                                />
                                                                {errors?.gradOther?.message && (
                                                                    <div>
                                                                        <Label className={'text-danger'}>
                                                                            {errors.gradOther.message as string}
                                                                        </Label>
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}
                                                        {year !== props.settings.reportingWindow.yearAvarageStart && (
                                                            <span>
                                                                {getDemographicsByGraduationYear(year)?.gradOther}
                                                            </span>
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                            <tr>
                                                <td></td>
                                                <td colSpan={(props.settings.reportingWindow.reportingWindow || 1) + 1}>
                                                    {errors?.ethnicityValidate?.message && (
                                                        <div>
                                                            <Label className={'text-danger'}>
                                                                {errors.ethnicityValidate.message as string}
                                                            </Label>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                    <Controller
                        name="demographicsId"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.demographicsId ??
                                    0
                                }
                            />
                        )}
                    />
                    <Controller
                        name="programId"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.programId ??
                                    props.program.programId
                                }
                            />
                        )}
                    />
                    <Controller
                        name="graduatingYear"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.graduatingYear ??
                                    defaultYear
                                }
                            />
                        )}
                    />
                    <Controller
                        name="createdBy"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.createdBy ??
                                    ''
                                }
                            />
                        )}
                    />
                    <Controller
                        name="dateCreated"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.dateCreated?.toString() ??
                                    new Date().toDateString()
                                }
                            />
                        )}
                    />
                    <Controller
                        name="editedBy"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.editedBy ??
                                    ''
                                }
                            />
                        )}
                    />
                    <Controller
                        name="lastEdited"
                        control={control}
                        render={({ field }) => (
                            <input
                                type="hidden"
                                {...field}
                                value={
                                    field.value ??
                                    getDemographicsByGraduationYear(
                                        props.settings.reportingWindow.yearAvarageStart || +props.reportYear - 1,
                                    )?.lastEdited?.toString() ??
                                    new Date().toDateString()
                                }
                            />
                        )}
                    />
                    {props.canEdit && (
                        <CardFooter className={'d-flex justify-content-between'}>
                            <Button
                                type={'button'}
                                color={'warning'}
                                onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                            >
                                <i className={'mdi mdi-arrow-left'} />
                                <span className={'mr-1'}>{`Previous`}</span>
                            </Button>

                            <Button
                                disabled={formSubmitting}
                                type={'button'}
                                color={'primary'}
                                onClick={() => submitWithoutValidation()}
                            >
                                <i className={'mdi mdi-content-save'} />
                                <span className={'ml-1'}>{`Save`}</span>
                            </Button>

                            <Button
                                disabled={formSubmitting}
                                type={'submit'}
                                color={'info'}
                                onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Forward)}
                            >
                                <span className={'mr-1'}>{`Save & Continue`}</span>
                                <i className={'mdi mdi-arrow-right'} />
                            </Button>
                        </CardFooter>
                    )}
                </Form>
            )}
        </Card>
    );
};

export default Demographics;
